import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Button, Chip, Grid, Box, Paper, Typography, TextField, List, ListItemText, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { TabPanelHeader } from "../../components/TabPanelHeader";
import { IWadTemplate, WadTemplateList, useGameConfigService } from "../../services/backend/GameConfigService";
import CodeMirror from "@uiw/react-codemirror";
import { tfidHighlightExtension } from "utils/TfidCodemirrorExtension";
import { YesNoCancelPopup } from "../../components/YesNoCancelPopup";
import { LoadingOverlay } from "../../components/LoadingOverlay";

import ListItemButton from "@mui/material/ListItemButton";

export interface WadTemplatesTabPanelProps {
  selectedBranch: string | undefined;
}

export const WadTemplatesTabPanel = ({ selectedBranch }: WadTemplatesTabPanelProps) => {
  const gameConfigService = useGameConfigService();

  const [templates, setTemplates] = useState<WadTemplateList>([]);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | null>(null);
  const [newLabelValue, setNewLabelValue] = useState<string>("");
  const [filePatternsText, setFilePatternsText] = useState<string>("");
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const fetchTemplates = async () => {
    if (!selectedBranch) return;

    try {
      setIsLoading(true);
      const fetchedTemplates = await gameConfigService.getWadTemplates(selectedBranch);
      setTemplates(fetchedTemplates);
      setSelectedTemplateIndex(null);
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to fetch wad templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedTemplateIndex !== null && templates[selectedTemplateIndex]) {
      setFilePatternsText(templates[selectedTemplateIndex].filePatterns.join("\n"));
    } else {
      setFilePatternsText("");
    }
  }, [selectedTemplateIndex, templates]);

  const handleAddTemplate = () => {
    const newTemplate: IWadTemplate = {
      labels: [],
      filePatterns: [],
      relatedAddressableGroup: "",
      description: "",
    };

    setTemplates([...templates, newTemplate]);
    setSelectedTemplateIndex(templates.length);
    setIsDirty(true);
  };

  const handleDeleteTemplate = () => {
    setDeleteConfirmationVisible(true);
  };

  const confirmDeleteTemplate = () => {
    if (selectedTemplateIndex !== null) {
      const newTemplates = [...templates];
      newTemplates.splice(selectedTemplateIndex, 1);
      setTemplates(newTemplates);
      setSelectedTemplateIndex(null);
      setDeleteConfirmationVisible(false);
      setIsDirty(true);
    }
  };

  const cancelDeleteTemplate = () => {
    setDeleteConfirmationVisible(false);
  };

  const handleSaveTemplates = async () => {
    if (!selectedBranch) return;

    try {
      setIsLoading(true);
      const newTemplates = [...templates];
      for (let i = 0; i < newTemplates.length; i++) {
        newTemplates[i].filePatterns = newTemplates[i].filePatterns.filter((line) => line.trim() !== "");
      }
      setTemplates(newTemplates);
      await gameConfigService.setWadTemplates(selectedBranch, newTemplates);
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to save wad templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLabel = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && newLabelValue.trim() !== "" && selectedTemplateIndex !== null) {
      const newTemplates = [...templates];
      newTemplates[selectedTemplateIndex].labels.push(newLabelValue.trim());
      setTemplates(newTemplates);
      setNewLabelValue("");
      setIsDirty(true);
    }
  };

  const handleDeleteLabel = (labelIndex: number) => {
    if (selectedTemplateIndex !== null) {
      const newTemplates = [...templates];
      newTemplates[selectedTemplateIndex].labels.splice(labelIndex, 1);
      setTemplates(newTemplates);
      setIsDirty(true);
    }
  };

  const handleFilePatternChange = useCallback(
    (value: string) => {
      setFilePatternsText(value);

      if (selectedTemplateIndex !== null) {
        const newTemplates = [...templates];
        newTemplates[selectedTemplateIndex].filePatterns = value.split("\n");
        setTemplates(newTemplates);
        setIsDirty(true);
      }
    },
    [selectedTemplateIndex, templates],
  );

  const handleRelatedAddressableGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedTemplateIndex !== null) {
      const newTemplates = [...templates];
      newTemplates[selectedTemplateIndex].relatedAddressableGroup = event.target.value;
      setTemplates(newTemplates);
      setIsDirty(true);
    }
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedTemplateIndex !== null) {
      const newTemplates = [...templates];
      newTemplates[selectedTemplateIndex].description = event.target.value;
      setTemplates(newTemplates);
      setIsDirty(true);
    }
  };

  if (!selectedBranch) {
    return (
      <Paper sx={{ p: 2, height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="subtitle1" color="textSecondary">
          Please select a branch to manage Wad templates.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TabPanelHeader>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveTemplates}
          sx={{ marginRight: 2, height: 44 }}
          disabled={!selectedBranch || !isDirty}
        >
          Save Templates
        </Button>
      </TabPanelHeader>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={4}>
          <Paper sx={{ p: 2, height: "70vh", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Wad Templates
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddTemplate}
                size="small"
                disabled={!selectedBranch}
              >
                Add Template
              </Button>
            </Box>
            <Divider />
            <List sx={{ flexGrow: 1, overflow: "auto" }}>
              {templates.map((template, index) => (
                <ListItemButton
                  key={index}
                  selected={selectedTemplateIndex === index}
                  onClick={() => setSelectedTemplateIndex(index)}
                >
                  <ListItemText
                    primary={template.description || `Template ${index + 1}`}
                    secondary={template.labels.join(", ")}
                  />
                </ListItemButton>
              ))}
            </List>
            <Divider />
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteTemplate}
                fullWidth
                disabled={selectedTemplateIndex === null}
              >
                Delete Template
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid size={8}>
          <Paper sx={{ p: 2, height: "70vh", display: "flex", flexDirection: "column" }}>
            {selectedTemplateIndex !== null && selectedTemplateIndex < templates.length ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Edit Template
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Labels
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
                    {templates[selectedTemplateIndex].labels.map((label, labelIndex) => (
                      <Chip
                        key={labelIndex}
                        label={label}
                        onDelete={() => handleDeleteLabel(labelIndex)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                  <TextField
                    label="Add Label"
                    value={newLabelValue}
                    onChange={(e) => setNewLabelValue(e.target.value)}
                    onKeyDown={handleAddLabel}
                    size="small"
                    fullWidth
                    helperText="Press Enter to add"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    File Patterns (one per line)
                  </Typography>
                  <CodeMirror
                    value={filePatternsText}
                    height="150px"
                    extensions={[tfidHighlightExtension]}
                    onChange={handleFilePatternChange}
                    style={{ border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                </Box>

                <TextField
                  label="Related Addressable Group"
                  value={templates[selectedTemplateIndex].relatedAddressableGroup || ""}
                  onChange={handleRelatedAddressableGroupChange}
                  fullWidth
                  margin="normal"
                />

                <TextField
                  label="Description"
                  value={templates[selectedTemplateIndex].description || ""}
                  onChange={handleDescriptionChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                />
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Typography variant="subtitle1" color="textSecondary">
                  Select a template or create a new one
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      <YesNoCancelPopup
        visible={deleteConfirmationVisible}
        title="Delete Template"
        message="Are you sure you want to delete this template?"
        onYes={confirmDeleteTemplate}
        onNo={cancelDeleteTemplate}
      />
      {isLoading && <LoadingOverlay />}
    </>
  );
};
